import React, { useState, useEffect } from 'react';
import styles from './AddOffer.module.css';

const AddOffer = ({ onSubmit, onCancel }) => {
  const [offerData, setOfferData] = useState({
    instruction: '',
    description: '',
    fromDate: '',
    toDate: '',
    image: null,
    imagePreview: null
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setIsFormValid(
      offerData.instruction.trim() !== '' &&
      offerData.description.trim() !== '' &&
      offerData.fromDate !== '' &&
      offerData.toDate !== ''
    );
  }, [offerData]);

  const handleFieldChange = (field, value) => {
    setOfferData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleImageUpload = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setOfferData(prev => ({ ...prev, image: file }));
        const reader = new FileReader();
        reader.onloadend = () => {
          setOfferData(prev => ({ ...prev, imagePreview: reader.result }));
        };
        reader.readAsDataURL(file);
      }
    };
    fileInput.click();
  };

  const removeImage = () => {
    setOfferData(prev => ({ ...prev, image: null, imagePreview: null }));
  };

  const handleSave = async () => {
    if (!isFormValid) return;
    setIsSubmitting(true);
    setError('');
    try {
      await new Promise(res => setTimeout(res, 500)); // Simulate async
      onSubmit({
        instruction: offerData.instruction,
        description: offerData.description,
        fromDate: offerData.fromDate,
        toDate: offerData.toDate,
        image: offerData.image
      });
    } catch (err) {
      setError('Failed to add offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.addCustomerContainer}>
      {/* Header */}
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={onCancel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h2 className={styles.headerTitle}>Add Offer</h2>
        </div>
        <div className={styles.headerActions}>
          <button
            className={`${styles.saveButton} ${!isFormValid || isSubmitting ? styles.disabled : ''}`}
            onClick={handleSave}
            disabled={!isFormValid || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12a9 9 0 11-6.219-8.56" />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Save
              </>
            )}
          </button>
        </div>
      </div>
      {/* Error Message */}
      {error && (
        <div className={styles.errorContainer}>
          <span className={styles.errorText}>{error}</span>
        </div>
      )}
      {/* Content */}
      <div className={styles.contentContainer}>
        <div className={styles.formSection}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Instruction *</label>
            <input
              type="text"
              value={offerData.instruction}
              onChange={e => handleFieldChange('instruction', e.target.value)}
              className={styles.fieldInput}
              placeholder="Enter offer instruction"
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Description *</label>
            <textarea
              value={offerData.description}
              onChange={e => handleFieldChange('description', e.target.value)}
              className={styles.fieldTextarea}
              placeholder="Enter offer description"
              rows={5}
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>From Date *</label>
            <input
              type="date"
              value={offerData.fromDate}
              onChange={e => handleFieldChange('fromDate', e.target.value)}
              className={styles.fieldInput}
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>To Date *</label>
            <input
              type="date"
              value={offerData.toDate}
              onChange={e => handleFieldChange('toDate', e.target.value)}
              className={styles.fieldInput}
            />
          </div>
        </div>
        <div className={styles.rightContent}>
          <div className={styles.imageSection}>
            <h3>Offer Image</h3>
            {offerData.imagePreview ? (
              <div className={styles.imageContainer}>
                <img src={offerData.imagePreview} alt="Preview" />
                <button
                  className={styles.removeButton}
                  onClick={removeImage}
                  type="button"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className={styles.uploadContainer}>
                <div className={styles.uploadIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7,10 12,15 17,10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </div>
                <p className={styles.uploadText}>Upload Offer Image</p>
                <button
                  className={styles.uploadButton}
                  onClick={handleImageUpload}
                  type="button"
                >
                  Choose File
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddOffer; 